import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Typography, Grid, Chip, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Alert
} from '@mui/material';
import { PlayArrow, Download, Refresh } from '@mui/icons-material';
import { scanCVE, getCVEScans, getCVEScanDetails, downloadCVEReport } from '../../services/api';

function CVEScanner() {
  const [scanType, setScanType] = useState('all');
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    loadScans();
    const interval = setInterval(loadScans, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadScans = async () => {
    try {
      const response = await getCVEScans();
      setScans(response.scans || []);
    } catch (error) {
      console.error('Failed to load scans:', error);
    }
  };

  const startScan = async () => {
    setScanning(true);
    try {
      await scanCVE({ scan_type: scanType });
      await loadScans();
      alert('CVE Scan gestartet!');
    } catch (error) {
      alert('Fehler beim Starten des Scans: ' + error.message);
    } finally {
      setScanning(false);
    }
  };

  const viewDetails = async (scanId) => {
    setLoading(true);
    try {
      const details = await getCVEScanDetails(scanId);
      setSelectedScan(details);
    } catch (error) {
      alert('Fehler beim Laden der Details');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (scanId) => {
    try {
      await downloadCVEReport(scanId);
    } catch (error) {
      alert('Fehler beim Download: ' + error.message);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'error',
      high: 'warning',
      medium: 'info',
      low: 'success'
    };
    return colors[severity] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      running: 'primary',
      failed: 'error',
      pending: 'warning'
    };
    return colors[status] || 'default';
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Neuen CVE Scan starten
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Scan Typ</InputLabel>
                    <Select value={scanType} onChange={(e) => setScanType(e.target.value)}>
                      <MenuItem value="all">Alle (Docker, System, NPM, Python)</MenuItem>
                      <MenuItem value="docker_container">Docker Container</MenuItem>
                      <MenuItem value="system_packages">System Pakete</MenuItem>
                      <MenuItem value="npm_dependencies">NPM Dependencies</MenuItem>
                      <MenuItem value="python_dependencies">Python Dependencies</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={scanning ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                    onClick={startScan}
                    disabled={scanning}
                  >
                    {scanning ? 'Scan läuft...' : 'Scan starten'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Scan Historie</Typography>
                <Button startIcon={<Refresh />} onClick={loadScans}>Aktualisieren</Button>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Gestartet</TableCell>
                      <TableCell align="center">Kritisch</TableCell>
                      <TableCell align="center">Hoch</TableCell>
                      <TableCell align="center">Mittel</TableCell>
                      <TableCell align="center">Niedrig</TableCell>
                      <TableCell align="center">Gesamt</TableCell>
                      <TableCell>Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scans.map((scan) => (
                      <TableRow key={scan.id}>
                        <TableCell>{scan.id}</TableCell>
                        <TableCell>{scan.scan_type.replace('_', ' ').toUpperCase()}</TableCell>
                        <TableCell>
                          <Chip label={scan.status} color={getStatusColor(scan.status)} size="small" />
                        </TableCell>
                        <TableCell>{new Date(scan.created_at).toLocaleString('de-DE')}</TableCell>
                        <TableCell align="center">
                          <Chip label={scan.critical_count} color="error" size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={scan.high_count} color="warning" size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={scan.medium_count} color="info" size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={scan.low_count} color="success" size="small" />
                        </TableCell>
                        <TableCell align="center">
                          <strong>{scan.total_vulnerabilities}</strong>
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => viewDetails(scan.id)}>Details</Button>
                          {scan.status === 'completed' && (
                            <Button size="small" startIcon={<Download />} onClick={() => downloadPDF(scan.id)}>
                              PDF
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {selectedScan && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Scan Details - ID: {selectedScan.id}
                </Typography>

                {loading ? (
                  <CircularProgress />
                ) : (
                  <TableContainer component={Paper} style={{ marginTop: '16px' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>CVE ID</TableCell>
                          <TableCell>Severity</TableCell>
                          <TableCell>Package</TableCell>
                          <TableCell>Installiert</TableCell>
                          <TableCell>Fix Version</TableCell>
                          <TableCell>CVSS Score</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedScan.findings || []).map((finding) => (
                          <TableRow key={finding.id}>
                            <TableCell>{finding.vulnerability_id}</TableCell>
                            <TableCell>
                              <Chip
                                label={finding.severity}
                                color={getSeverityColor(finding.severity)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{finding.package_name}</TableCell>
                            <TableCell>{finding.installed_version}</TableCell>
                            <TableCell>{finding.fixed_version || 'N/A'}</TableCell>
                            <TableCell>{finding.cvss_score || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default CVEScanner;
